import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BehaviorPatternMatrix = ({ data }) => {
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [viewMode, setViewMode] = useState('heatmap'); // heatmap, list

  if (!data || data?.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
        No behavior pattern data available
      </div>
    );
  }

  const getEngagementColor = (score) => {
    if (score >= 80) return 'bg-success text-white';
    if (score >= 60) return 'bg-primary text-white';
    if (score >= 40) return 'bg-warning text-white';
    if (score >= 20) return 'bg-orange-500 text-white';
    return 'bg-error text-white';
  };

  const getEngagementIntensity = (score) => {
    if (score >= 80) return 'bg-success/90';
    if (score >= 60) return 'bg-success/60';
    if (score >= 40) return 'bg-warning/60';
    if (score >= 20) return 'bg-orange-500/60';
    return 'bg-error/60';
  };

  const getPatternIcon = (pattern) => {
    const icons = {
      'daily-active': 'Zap',
      'weekly-active': 'Calendar',
      'feature-explorer': 'Compass',
      'premium-curious': 'Eye',
      'dormant': 'Moon',
      'at-risk': 'AlertTriangle'
    };
    return icons?.[pattern] || 'User';
  };

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Grid" size={16} className="text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Behavior Patterns</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('heatmap')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-smooth ${
              viewMode === 'heatmap' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Heatmap
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-smooth ${
              viewMode === 'list' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            List
          </button>
        </div>
      </div>

      {viewMode === 'heatmap' ? (
        <div className="space-y-3">
          {/* Heatmap Grid */}
          {data?.map((pattern, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name={getPatternIcon(pattern?.patternId)} size={16} className="text-foreground" />
                  <span className="text-sm font-medium text-foreground">{pattern?.patternName}</span>
                  <span className="text-xs text-muted-foreground">({pattern?.userCount?.toLocaleString()} users)</span>
                </div>
                <span className="text-xs font-bold text-foreground">{pattern?.avgEngagementScore}/100</span>
              </div>

              {/* Feature Engagement Heatmap */}
              <div className="grid grid-cols-7 gap-1">
                {pattern?.featureEngagement?.map((feature, fIndex) => (
                  <div
                    key={fIndex}
                    className={`relative h-12 rounded ${getEngagementIntensity(feature?.score)} cursor-pointer hover:ring-2 hover:ring-primary transition-all group`}
                    title={`${feature?.featureName}: ${feature?.score}%`}
                  >
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-1">
                      <Icon name={feature?.icon || 'Circle'} size={14} className="text-white opacity-80" />
                      <span className="text-[10px] font-bold text-white mt-0.5">{feature?.score}%</span>
                    </div>
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      {feature?.featureName}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pattern Insights */}
              {pattern?.insights && (
                <div className="p-2 bg-muted/30 rounded text-xs text-muted-foreground">
                  <Icon name="Info" size={12} className="inline mr-1" />
                  {pattern?.insights}
                </div>
              )}
            </div>
          ))}

          {/* Legend */}
          <div className="flex items-center gap-4 text-xs mt-4 p-3 bg-card rounded-lg border border-border">
            <span className="text-muted-foreground font-medium">Engagement Level:</span>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-success"></div>
              <span className="text-muted-foreground">80-100%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-primary"></div>
              <span className="text-muted-foreground">60-79%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-warning"></div>
              <span className="text-muted-foreground">40-59%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-500"></div>
              <span className="text-muted-foreground">20-39%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-error"></div>
              <span className="text-muted-foreground">0-19%</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* List View */}
          {data?.map((pattern, index) => (
            <div
              key={index}
              onClick={() => setSelectedPattern(pattern)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedPattern?.patternId === pattern?.patternId
                  ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getEngagementColor(pattern?.avgEngagementScore)}`}>
                    <Icon name={getPatternIcon(pattern?.patternId)} size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{pattern?.patternName}</h3>
                    <p className="text-xs text-muted-foreground">{pattern?.userCount?.toLocaleString()} users • {pattern?.percentageOfTotal}% of total</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Engagement Score</p>
                  <p className="text-2xl font-bold text-foreground">{pattern?.avgEngagementScore}</p>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Avg Sessions/Week</p>
                  <p className="text-sm font-bold text-foreground">{pattern?.avgSessionsPerWeek}</p>
                </div>
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Features Used</p>
                  <p className="text-sm font-bold text-primary">{pattern?.avgFeaturesUsed}</p>
                </div>
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Conversion Rate</p>
                  <p className="text-sm font-bold text-success">{pattern?.conversionRate}%</p>
                </div>
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Retention</p>
                  <p className="text-sm font-bold text-foreground">{pattern?.retentionRate}%</p>
                </div>
              </div>

              {/* Top Features */}
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-2">Most Used Features:</p>
                <div className="flex flex-wrap gap-2">
                  {pattern?.topFeatures?.map((feature, fIndex) => (
                    <span
                      key={fIndex}
                      className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Behavior Insights Summary */}
      <div className="p-4 bg-gradient-to-br from-primary/10 to-success/10 rounded-lg border border-primary/20">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Brain" size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">Behavior Pattern Insights</h3>
        </div>
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
            <p className="text-xs text-foreground">Daily active users show 3.5x higher conversion rate than weekly users</p>
          </div>
          <div className="flex items-start gap-2">
            <Icon name="Target" size={14} className="text-primary mt-0.5" />
            <p className="text-xs text-foreground">Feature explorers (using 5+ features) have 89% retention after 30 days</p>
          </div>
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={14} className="text-warning mt-0.5" />
            <p className="text-xs text-foreground">At-risk users can be re-engaged with personalized feature recommendations</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BehaviorPatternMatrix;